import { useEffect, useState, useCallback } from "react";
import { useFeatureFlags, FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";
import { useAuthStore } from "@src/features/auth/auth-store";

export type Auth0UpsellMode = "off" | "soft" | "hard";

export interface Auth0UpsellConfig {
  mode: Auth0UpsellMode;
  title: string;
  message: string;
  ctaText: string;
}

const DEFAULT_CONFIG: Auth0UpsellConfig = {
  mode: "off",
  title: "Upgrade Your Login",
  message: "Switch to our new secure login for enhanced security and a better experience.",
  ctaText: "Upgrade Now",
};

/**
 * Hook to manage Auth0 upsell banner state based on feature flag and actual auth token type
 *
 * Banner visibility logic:
 * 1. If mode is "off" → never show banners
 * 2. If user has Auth0 token → never show banners (user already migrated)
 * 3. If mode is "soft"/"hard" AND user has no Auth0 token → show banners
 *
 * The banner should show based on the ACTUAL token type being used, not just the preference.
 * The preference is just a hint for what to use on next login, but the actual token determines
 * whether the user is currently on legacy auth.
 *
 * The feature flag payload should be a JSON object with:
 * - mode: "off" | "soft" | "hard"
 * - title: string (optional, has default)
 * - message: string (optional, has default)
 * - ctaText: string (optional, has default)
 */
export const useAuth0Upsell = () => {
  const [config, setConfig] = useState<Auth0UpsellConfig>(DEFAULT_CONFIG);
  const [isDismissed, setIsDismissed] = useState(false);
  const { isInitialized, getFlagPayload, flags } = useFeatureFlags();
  const legacyAuth = useAuthStore((state) => state.legacyAuth);
  const accessToken = useAuthStore((state) => state.accessToken);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // User is on legacy flow if they have a legacy token but NO Auth0 token
  // If they have both tokens, they've already upgraded (Auth0 takes precedence)
  // If they only have Auth0 token, they're not on legacy flow
  const isOnLegacyFlow = isAuthenticated && legacyAuth !== null && accessToken === null;

  // Load feature flag config when initialized
  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    try {
      const flagPayload = getFlagPayload<Partial<Auth0UpsellConfig>>(FEATURE_FLAGS.AUTH0_MIGRATION_BANNER);

      if (!flagPayload || typeof flagPayload !== "object") {
        setConfig(DEFAULT_CONFIG);
        return;
      }

      // Validate mode
      const validModes: Auth0UpsellMode[] = ["off", "soft", "hard"];
      const mode =
        validModes.includes(flagPayload.mode as Auth0UpsellMode) ? (flagPayload.mode as Auth0UpsellMode) : "off";

      const newConfig = {
        mode,
        title: flagPayload.title || DEFAULT_CONFIG.title,
        message: flagPayload.message || DEFAULT_CONFIG.message,
        ctaText: flagPayload.ctaText || DEFAULT_CONFIG.ctaText,
      };
      setConfig(newConfig);
    } catch (error) {
      console.error("[Auth0 Upsell] Error reading config:", error);
      setConfig(DEFAULT_CONFIG);
    }
  }, [isInitialized, getFlagPayload, flags]);

  const dismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  // Only show banners if user is authenticated, on legacy flow, and not dismissed
  // Note: We don't need to wait for preference to load - we check actual token type
  const canShowBanner = isAuthenticated && isOnLegacyFlow && !isDismissed;
  const showSoftBanner = config.mode === "soft" && canShowBanner;
  const showHardModal = config.mode === "hard" && canShowBanner;

  return {
    config,
    showSoftBanner,
    showHardModal,
    isEnabled: config.mode !== "off",
    isOnLegacyFlow,
    dismiss,
  };
};
